import { getGraphClient } from './graphClient';

// Plus besoin de constante BASE, le client gère la racine

// Les appels sont désormais effectués via Microsoft Graph SDK

// Fonction utilitaire pour retry automatique
const retryOperation = async (operation, maxRetries = 3, delayMs = 1000) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await operation();
    } catch (error) {

      // Si c'est la dernière tentative, lancer l'erreur
      if (i === maxRetries - 1) {
        throw error;
      }
      
      // Si c'est une erreur d'authentification, attendre plus longtemps
      const isAuthError = error.response?.status === 401 || 
                         error.message?.includes('interaction_in_progress') ||
                         error.message?.includes('token');
      
      const delay = isAuthError ? delayMs * (i + 2) : delayMs * (i + 1);

      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

const graphService = {
  async listRootFiles() {

    return retryOperation(async () => {
      const client = await getGraphClient();

      const res = await client.api('/me/drive/root/children').get();

      return res.value;
    });
  },

  async listFolderFiles(folderId) {

    return retryOperation(async () => {
      const client = await getGraphClient();

      const res = await client.api(`/me/drive/items/${folderId}/children`).get();

      return res.value;
    });
  },

  async getDownloadUrl(fileId) {

    return retryOperation(async () => {
      const client = await getGraphClient();

      const res = await client.api(`/me/drive/items/${fileId}`).get();
      const downloadUrl = res['@microsoft.graph.downloadUrl'];
      
      if (!downloadUrl) {
        throw new Error('URL de téléchargement non disponible pour ce fichier');
      }

      return downloadUrl;
    });
  },

  // **NOUVELLE MÉTHODE : Vérifier la connectivité OneDrive**
  async checkOneDriveAccess() {

    try {
      const client = await getGraphClient();

      const res = await client.api('/me/drive').get();

      return {
        success: true,
        driveInfo: {
          id: res.id,
          name: res.name,
          driveType: res.driveType
        }
      };
    } catch (error) {

      const isAuthError = error.statusCode === 401 || error.message?.includes('token');
      
      return {
        success: false,
        error: error.message,
        needsReconnection: isAuthError,
        statusCode: error.statusCode
      };
    }
  },

  async searchUsers(searchTerm = '', maxResults = 10) {
    try {
      const client = await getGraphClient();

      let request = client.api('/users').top(maxResults).select('id,displayName,mail,jobTitle,userPrincipalName');

      if (searchTerm && searchTerm.length >= 2) {
        const escaped = searchTerm.replace(/'/g, "''");
        const filter = `startswith(displayName,'${escaped}') or startswith(mail,'${escaped}') or startswith(userPrincipalName,'${escaped}')`;
        request = request.filter(filter);
      }

      const res = await request.get();

      return res.value.map(user => ({
        id: user.id,
        displayName: user.displayName,
        email: user.mail || user.userPrincipalName,
        title: user.jobTitle,
        photoURL: null
      }));
    } catch (error) {

      return [];
    }
  },

  // **NOUVELLES MÉTHODES CALENDAR + ONEDRIVE POUR TEAMS MEETINGS**

  async getTeamsMeetingsFromCalendar(months = 3) {
    console.log('📅 Récupération des réunions Teams depuis le calendrier');

    return retryOperation(async () => {
      const client = await getGraphClient();

      const endDate = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - months);

      // Récupérer l'email de l'utilisateur actuel pour déterminer s'il est organisateur
      const userProfile = await client.api('/me').select('mail,userPrincipalName').get();
      const currentUserEmail = userProfile.mail || userProfile.userPrincipalName;

      const res = await client.api('/me/events')
        .filter(`start/dateTime ge '${startDate.toISOString()}' and end/dateTime le '${endDate.toISOString()}'`)
        .select('id,subject,start,end,onlineMeetingUrl,organizer,attendees,bodyPreview,location,createdDateTime,isOnlineMeeting')
        .top(100)
        .orderby('start/dateTime desc')
        .get();

      // Filtrer côté client pour ne garder que les réunions en ligne
      const onlineMeetings = res.value.filter(event => event.isOnlineMeeting === true);
      console.log(`✅ ${onlineMeetings.length}/${res.value.length} réunions Teams en ligne trouvées dans le calendrier`);

      return onlineMeetings.map(event => ({
        id: event.id,
        subject: event.subject || 'Réunion sans titre',
        startDateTime: event.start.dateTime,
        endDateTime: event.end.dateTime,
        onlineMeetingUrl: event.onlineMeetingUrl,
        organizer: event.organizer,
        attendees: event.attendees || [],
        createdDateTime: event.createdDateTime,
        isOrganizer: event.organizer?.emailAddress?.address?.toLowerCase() === currentUserEmail?.toLowerCase(),
        type: 'calendar_event',
        hasRecording: false // À vérifier via OneDrive
      }));
    });
  },

  async findRecordingForMeeting(meetingSubject, meetingDate, meetingId) {
    console.log('🔍 Recherche d\'enregistrement dans OneDrive pour:', { meetingSubject, meetingDate });

    return retryOperation(async () => {
      const client = await getGraphClient();

      try {
        // Option 1 : Chercher dans le dossier Recordings spécifique
        try {
          const recordingsFolder = await client.api('/me/drive/root:/Recordings')
            .get();

          if (recordingsFolder) {
            console.log('📁 Dossier Recordings trouvé, recherche des fichiers...');

            const files = await client.api(`/me/drive/items/${recordingsFolder.id}/children`)
              .filter('file ne null')
              .select('id,name,createdDateTime,size,webUrl,@microsoft.graph.downloadUrl,parentReference')
              .get();

            // Filtrer les fichiers vidéo et essayer de faire correspondre
            const videoFiles = files.value.filter(file => {
              const isVideo = /\.(mp4|mkv|avi|mov|wmv)$/i.test(file.name);
              return isVideo;
            });

            console.log(`📹 ${videoFiles.length} fichiers vidéo trouvés dans Recordings`);

            // Correspondance par nom ou date
            if (meetingSubject) {
              const nameMatches = videoFiles.filter(file => {
                const fileName = file.name.toLowerCase();
                const subject = meetingSubject.toLowerCase();
                return fileName.includes(subject) || subject.includes(fileName.replace(/\.[^/.]+$/, ""));
              });

              if (nameMatches.length > 0) {
                console.log(`✅ Correspondance par nom trouvée: ${nameMatches.length} fichier(s)`);
                return nameMatches;
              }
            }

            // Correspondance par date (même jour)
            if (meetingDate) {
              const dateStr = meetingDate.split('T')[0];
              const dateMatches = videoFiles.filter(file => {
                const fileDate = file.createdDateTime.split('T')[0];
                return fileDate === dateStr;
              });

              if (dateMatches.length > 0) {
                console.log(`✅ Correspondance par date trouvée: ${dateMatches.length} fichier(s)`);
                return dateMatches;
              }
            }

            // Retourner tous les fichiers récents si pas de correspondance exacte
            return videoFiles.slice(0, 10);
          }
        } catch (folderError) {
          console.log('📁 Dossier Recordings non trouvé, recherche globale...');
        }

        // Option 2 : Recherche globale de fichiers vidéo récents
        const searchQuery = meetingSubject ?
          meetingSubject.split(' ').slice(0, 3).join(' ') : // Premiers mots du titre
          'Recording';

        const searchResults = await client.api(`/me/drive/root/search(q='${searchQuery}')`)
          .filter('file ne null')
          .select('id,name,createdDateTime,size,webUrl,@microsoft.graph.downloadUrl,parentReference')
          .get();

        const videoFiles = searchResults.value.filter(file =>
          /\.(mp4|mkv|avi|mov|wmv)$/i.test(file.name)
        );

        console.log(`🔍 Recherche globale: ${videoFiles.length} fichiers vidéo trouvés`);

        return videoFiles.slice(0, 5); // Limiter à 5 résultats

      } catch (error) {
        console.warn('❌ Erreur lors de la recherche d\'enregistrements:', error);
        return [];
      }
    });
  },

  async getMeetingsWithRecordings(months = 3) {
    console.log('🔍 Recherche des réunions Teams avec enregistrements (Calendar + OneDrive)');

    try {
      // 1. Récupérer toutes les réunions Teams depuis le calendrier
      const meetings = await this.getTeamsMeetingsFromCalendar(months);

      // 2. Pour chaque réunion, chercher un enregistrement potentiel
      const meetingsWithRecordings = await Promise.all(
        meetings.map(async (meeting) => {
          try {
            const recordings = await this.findRecordingForMeeting(
              meeting.subject,
              meeting.startDateTime,
              meeting.id
            );

            return {
              ...meeting,
              hasRecording: recordings.length > 0,
              recordings: recordings,
              recordingCount: recordings.length
            };
          } catch (error) {
            console.warn(`❌ Erreur lors de la recherche d'enregistrements pour ${meeting.subject}:`, error.message);
            return {
              ...meeting,
              hasRecording: false,
              recordings: [],
              recordingCount: 0,
              error: error.message
            };
          }
        })
      );

      const withRecordings = meetingsWithRecordings.filter(m => m.hasRecording);
      console.log(`📊 ${withRecordings.length}/${meetings.length} réunions avec enregistrements potentiels`);

      return meetingsWithRecordings;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des réunions avec enregistrements:', error);
      throw error;
    }
  },

  async downloadRecordingFromOneDrive(fileId, fileName) {
    console.log('⬇️ Téléchargement de l\'enregistrement OneDrive:', fileId);

    return retryOperation(async () => {
      const client = await getGraphClient();

      // Récupérer les métadonnées et le lien de téléchargement
      const file = await client.api(`/me/drive/items/${fileId}`)
        .select('id,name,size,createdDateTime,@microsoft.graph.downloadUrl')
        .get();

      if (!file['@microsoft.graph.downloadUrl']) {
        throw new Error('URL de téléchargement non disponible pour ce fichier');
      }

      // Télécharger le contenu binaire
      const response = await fetch(file['@microsoft.graph.downloadUrl']);

      if (!response.ok) {
        throw new Error(`Erreur lors du téléchargement: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();

      // Créer un objet File compatible avec le système existant
      const finalFileName = fileName || file.name;
      const fileObj = new File([blob], finalFileName, {
        type: blob.type || 'video/mp4',
        lastModified: new Date(file.createdDateTime).getTime()
      });

      // Ajouter les métadonnées nécessaires
      fileObj.size = blob.size;
      fileObj.fromTeams = true;
      fileObj.fromOneDrive = true;
      fileObj.oneDriveFileId = fileId;

      console.log('✅ Enregistrement OneDrive téléchargé:', {
        fileName: fileObj.name,
        size: `${(fileObj.size / (1024 * 1024)).toFixed(2)} MB`,
        type: fileObj.type
      });

      return fileObj;
    });
  }
};

export default graphService; 